import { useState } from 'react';
import { RESERVATION_VEHICLE_LIMIT, type Reservation } from '@repo/domain';
import { useReservationActions, useVehicles } from '@repo/domain-service';
import { tr } from '@repo/locale';
import type { TestId } from '@repo/test-id';

import VehicleCreationModal from '@features/reservation/components/vehicle/VehicleCreationModal';
import VehicleSelectionModal from '@features/reservation/components/vehicle/VehicleSelectionModal';

import { handleRepositoryError } from '@shared/utils/handleRepositoryError';

interface Props {
  testId: TestId;
  reservation: Reservation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Each tick is saved at once; the backend decides what is allowed (limit, last vehicle, sealed vehicle).
export default function VehicleChangeContainer({ testId, reservation, open, onOpenChange }: Props) {
  const { vehicles } = useVehicles();
  const {
    addVehicleToReservation,
    addingVehicleToReservation,
    removeVehicleFromReservation,
    removingVehicleFromReservation,
  } = useReservationActions();
  const [creationDrawerOpen, setCreationDrawerOpen] = useState<boolean>(false);

  const reservationVehicles = reservation.vehicles ?? (reservation.vehicle ? [reservation.vehicle] : []);

  const handleVehicleChangeError = (error: unknown) => {
    handleRepositoryError(error, {
      fallback: tr('reservations:summary.vehicle_selection.multiple.error'),
      testId,
      businessCodes: {
        reservationFailedDueToVehicleLimitReached: tr('reservations:summary.vehicle_selection.multiple.limit_reached', {
          max: RESERVATION_VEHICLE_LIMIT,
        }),
        reservationFailedDueToRegistrationNumberAlreadyAdded: tr(
          'reservations:summary.vehicle_selection.multiple.already_added'
        ),
        reservationFailedDueToVehicleSealed: tr('reservations:summary.vehicle_selection.multiple.sealed'),
        reservationFailedDueToLastVehicle: tr('reservations:summary.vehicle_selection.multiple.last_vehicle'),
      },
    });
  };

  const handleVehicleAdd = async (vehicleId: string) => {
    try {
      await addVehicleToReservation({ reservationId: reservation.id, vehicleId });
    } catch (error) {
      handleVehicleChangeError(error);
    }
  };

  const handleVehicleRemove = async (vehicleId: string) => {
    try {
      await removeVehicleFromReservation({ reservationId: reservation.id, vehicleId });
    } catch (error) {
      handleVehicleChangeError(error);
    }
  };

  const handleAddNewVehicle = () => {
    onOpenChange(false);
    setCreationDrawerOpen(true);
  };

  const handleVehicleAdded = () => {
    onOpenChange(true);
  };

  return (
    <>
      <VehicleSelectionModal
        testId={testId.append('vehicleSelection')}
        open={open}
        vehicles={vehicles}
        selectedVehicleIds={reservationVehicles.map((reservationVehicle) => reservationVehicle.id)}
        disabled={addingVehicleToReservation || removingVehicleFromReservation}
        onVehicleAdd={handleVehicleAdd}
        onVehicleRemove={handleVehicleRemove}
        onOpenChange={onOpenChange}
        onAddNewVehicle={handleAddNewVehicle}
      />
      <VehicleCreationModal
        testId={testId.append('vehicleCreation')}
        open={creationDrawerOpen}
        onOpenChange={setCreationDrawerOpen}
        onVehicleAdded={handleVehicleAdded}
      />
    </>
  );
}
